import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Weight } from "lucide-react";
import ExpiryDateInput from "@/components/ExpiryDateInput";
import MatchSuggestions from "@/components/MatchSuggestions";
import FieldRow from "./FieldRow";
import { CATEGORIES } from "./constants";

// Round to 2 decimals avoiding JS banker's rounding (so 16.425 → 16.43, not 16.42)
const round2 = (n) => Math.round(n * 100) / 100;

// Parse "0.522 kg" -> 0.522 ; "522 g" -> 0.522 ; returns 0 if not parsable
function parseWeightKg(v) {
  if (!v) return 0;
  const s = String(v).trim().toLowerCase().replace(",", ".");
  const m = s.match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
  if (!m) return 0;
  const num = parseFloat(m[1]);
  const unit = (m[2] || "kg").trim();
  if (unit === "g" || unit === "gr") return num / 1000;
  if (unit === "hg") return num / 10;
  return num;
}

export default function ProductRowFields({ row, onChange, intraScanLink }) {
  // is_weight_unit dall'AI (DDT con U.M.=KG): forza modalità Peso e blocca il toggle a Pezzo
  const lockedToWeight = !!row.is_weight_unit;

  // Infer mode: se AI ha rilevato U.M.=KG → forza peso. Altrimenti se ha estratto price_per_kg → peso. Fallback parse weight.
  const defaultMode = useMemo(() => {
    if (lockedToWeight) return "weight";
    if (row.product_mode) return row.product_mode;
    if (row.price_per_kg) return "weight";
    const kg = parseWeightKg(row.weight);
    if (kg > 0 && kg < 50 && row.weight && /kg|g|etto|hg/i.test(row.weight)) return "weight";
    return "piece";
  }, [row, lockedToWeight]);
  const isWeight = defaultMode === "weight";

  // €/kg effettivo (in modalità peso): preferisci price_per_kg, fallback unit_price (caso AI ha messo prezzo nella chiave generica)
  const grossPricePerKg = useMemo(() => {
    if (!isWeight) return 0;
    return Number(row.price_per_kg) || Number(row.unit_price) || 0;
  }, [isWeight, row.price_per_kg, row.unit_price]);
  const grossUnitPrice = useMemo(() => {
    if (isWeight) return 0;
    return Number(row.unit_price) || Number(row.price_per_kg) || 0;
  }, [isWeight, row.unit_price, row.price_per_kg]);

  const discountPct = Number(row.discount_pct) || 0;
  const discountFactor = 1 - Math.max(0, Math.min(100, discountPct)) / 100;
  const netPricePerKg = grossPricePerKg * discountFactor;
  const netUnitPrice = grossUnitPrice * discountFactor;

  // Compute live totals (gross, discount amount, net)
  const totals = useMemo(() => {
    if (isWeight) {
      const kg = parseWeightKg(row.weight);
      if (!kg || !grossPricePerKg) return null;
      const gross = kg * grossPricePerKg;
      const net = gross * discountFactor;
      return { gross: round2(gross).toFixed(2), discount: round2(gross - net).toFixed(2), net: round2(net).toFixed(2) };
    }
    const q = Number(row.stock) || 0;
    if (!q || !grossUnitPrice) return null;
    const gross = q * grossUnitPrice;
    const net = gross * discountFactor;
    return { gross: round2(gross).toFixed(2), discount: round2(gross - net).toFixed(2), net: round2(net).toFixed(2) };
  }, [row.weight, row.stock, grossPricePerKg, grossUnitPrice, discountFactor, isWeight]);

  const setMode = (m) => {
    if (lockedToWeight) return; // can't switch
    if (m === "weight") {
      onChange({ product_mode: "weight" });
    } else {
      onChange({ product_mode: "piece", price_per_kg: null });
    }
  };

  return (
    <div className="space-y-2.5 pl-0 sm:pl-7 min-w-0">
      <FieldRow label="Nome *">
        <Input value={row.name || ""} onChange={(e) => onChange({ name: e.target.value })} />
      </FieldRow>

      <MatchSuggestions
        name={row.name}
        category={row.category}
        batch={row.batch}
        expiryDate={row.expiry_date}
        expirySource={row.__expiry_source}
        linkedId={row.__link_to_id}
        onLink={(p) => onChange({ __link_to_id: p.id, __match_rejected: false })}
        onCreate={() => onChange({ __link_to_id: null, __match_rejected: true })}
        onAdoptLotFields={(patch) => onChange({ ...patch, __expiry_source: "adopted" })}
      />

      {/* Avviso intra-scan: questa riga verrà agganciata a una riga precedente
          dello stesso documento (stesso nome+categoria). Testo neutro, sparisce
          se l'operatore clicca "Crea nuovo prodotto" nel match (che setta __match_rejected). */}
      {intraScanLink && !row.__match_rejected && !row.__link_to_id && (
        <div
          data-testid={`intra-scan-link-notice-${row.__id}`}
          className="text-[11px] text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] rounded-md px-2.5 py-1.5"
        >
          Stesso prodotto della riga #{intraScanLink.targetIndex + 1} — il lotto{" "}
          <span className="font-mono">{intraScanLink.targetBatch || "senza numero"}</span>{" "}
          si aggiunge alla stessa scheda.
        </div>
      )}

      {(row.plu !== undefined || row.supplier_code !== undefined || row.code) && (
        <div className="grid grid-cols-2 gap-2">
          <FieldRow label="PLU">
            <Input
              value={row.plu || ""}
              onChange={(e) => onChange({ plu: e.target.value })}
              placeholder="Es: 270"
              inputMode="numeric"
              className="font-mono text-xs"
            />
          </FieldRow>
          <FieldRow label="Codice fornitore">
            <Input
              value={row.supplier_code || ""}
              onChange={(e) => onChange({ supplier_code: e.target.value.toUpperCase() })}
              placeholder="Es: 04FC0200V"
              className="font-mono text-xs uppercase"
            />
          </FieldRow>
        </div>
      )}

      <FieldRow label="Categoria *">
        <Select value={row.category || ""} onValueChange={(v) => onChange({ category: v })}>
          <SelectTrigger>
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="Lotto">
        <Input value={row.batch || ""} onChange={(e) => onChange({ batch: e.target.value })} className="font-mono" />
      </FieldRow>

      <FieldRow label="Scadenza">
        <div className="space-y-1.5">
          <ExpiryDateInput
            value={row.expiry_date}
            onChange={(v) => onChange({ expiry_date: v, __expiry_source: "manual" })}
            category={row.category}
            name={row.name}
          />
          {row.__expiry_source === "learned" && (
            <div data-testid="expiry-badge-learned" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#E8F3EC] text-[#2B5A3F] border border-[#C5E1D1]">
              🎓 Imparata{row.suggested_shelf_life_days ? ` · ${row.suggested_shelf_life_days}gg` : ""}
              {row.learned_confirmation_count ? ` · ${row.learned_confirmation_count} conferme` : ""}
            </div>
          )}
          {row.__expiry_source === "ai_suggested" && (
            <div data-testid="expiry-badge-ai" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#E8F3EC] text-[#2B5A3F] border border-[#C5E1D1]">
              🤖 Stimata AI{row.suggested_shelf_life_days ? ` · ${row.suggested_shelf_life_days}gg` : ""}
              {row.suggested_shelf_life_reason ? ` · ${row.suggested_shelf_life_reason}` : ""}
            </div>
          )}
          {row.__expiry_source === "category_default" && (
            <div data-testid="expiry-badge-default" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FDF6E3] text-[#8B6518] border border-[#F2DEB0]">
              📊 Default categoria{row.suggested_shelf_life_days ? ` · ${row.suggested_shelf_life_days}gg` : ""}
            </div>
          )}
          {row.__expiry_source === "document" && (
            <div data-testid="expiry-badge-doc" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#F4F1ED] text-[#5C5F5A] border border-[#EAE7E1]">
              📄 Letta dal documento
            </div>
          )}
          {row.__expiry_source === "adopted" && (
            <div data-testid="expiry-badge-adopted" className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#E8F3EC] text-[#2B5A3F] border border-[#C5E1D1]">
              🔗 Adottata dal lotto esistente
            </div>
          )}
          {!row.expiry_date && row.suggested_expiry_date && row.__expiry_source !== "ai_suggested" && row.__expiry_source !== "category_default" && (
            <button
              type="button"
              data-testid="expiry-suggest-apply"
              onClick={() => onChange({
                expiry_date: row.suggested_expiry_date,
                __expiry_source: row.suggested_source === "ai" ? "ai_suggested" : "category_default",
              })}
              className="text-[10px] underline text-[#2B5A3F] hover:text-[#1F4530]"
            >
              📅 Usa scadenza suggerita ({row.suggested_shelf_life_days}gg)
            </button>
          )}
        </div>
      </FieldRow>

      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-2 p-2 rounded-md bg-[#F4F1ED] border border-[#EAE7E1]">
        <Weight size={14} className="text-[#2E4A3B] flex-shrink-0" />
        <span className="text-xs font-medium min-w-0 flex-1 break-words">
          Venduto a:
          {lockedToWeight && (
            <span className="ml-1.5 text-[10px] font-normal text-[#5C5F5A]">(rilevato KG dal DDT)</span>
          )}
        </span>
        <div className="flex gap-1 flex-shrink-0">
          <button
            type="button"
            data-testid={`product-mode-weight-${row.__id}`}
            onClick={() => setMode("weight")}
            className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
              isWeight
                ? "bg-[#2E4A3B] text-white"
                : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
            }`}
          >
            Peso (kg)
          </button>
          <button
            type="button"
            data-testid={`product-mode-piece-${row.__id}`}
            onClick={() => setMode("piece")}
            disabled={lockedToWeight}
            className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
              !isWeight
                ? "bg-[#2E4A3B] text-white"
                : lockedToWeight
                ? "bg-white text-[#C5C5C0] border border-[#EAE7E1] cursor-not-allowed opacity-50"
                : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
            }`}
          >
            Pezzo
          </button>
        </div>
      </div>

      {isWeight ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <FieldRow label="Peso (kg) *">
              <Input
                placeholder="0.522 kg"
                value={row.weight || ""}
                onChange={(e) => onChange({ weight: e.target.value })}
                data-testid={`product-weight-input-${row.__id}`}
              />
            </FieldRow>
            <FieldRow label="Costo acquisto €/kg lordo *">
              <Input
                type="number"
                step="0.01"
                value={grossPricePerKg || ""}
                onChange={(e) => onChange({ price_per_kg: e.target.value, unit_price: null })}
                data-testid={`product-price-per-kg-${row.__id}`}
                className="font-mono font-semibold"
              />
            </FieldRow>
          </div>
        </>
      ) : (
        <>
          <FieldRow label="Formato / Confezione">
            <Input
              placeholder="es. 90g, 1L"
              value={row.weight || ""}
              onChange={(e) => onChange({ weight: e.target.value })}
            />
          </FieldRow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <FieldRow label={row.__link_to_id ? "Pezzi da aggiungere *" : "Quantità (pezzi) *"}>
              <Input
                type="number"
                min="1"
                value={row.stock || 1}
                onChange={(e) => onChange({ stock: e.target.value })}
                data-testid={`product-qty-input-${row.__id}`}
              />
            </FieldRow>
            <FieldRow label="Costo acquisto (€/pz lordo) *">
              <Input
                type="number"
                step="0.01"
                value={grossUnitPrice || ""}
                onChange={(e) => onChange({ unit_price: e.target.value })}
                data-testid={`product-unit-price-${row.__id}`}
                className="font-mono font-semibold"
              />
            </FieldRow>
          </div>
        </>
      )}

      {/* Discount field */}
      <FieldRow label="Sconto applicato (%)">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.5"
              min="0"
              max="100"
              placeholder="0"
              value={row.discount_pct ?? 0}
              onChange={(e) => onChange({ discount_pct: e.target.value, __discount_source: "manual" })}
              data-testid={`product-discount-${row.__id}`}
              className="font-mono"
            />
            <span className="text-xs text-[#8E918C] whitespace-nowrap">% sconto</span>
          </div>
          {row.__discount_source && Number(row.discount_pct) > 0 && (
            <div className="text-[10px] text-[#8E918C] flex items-center gap-1">
              {row.__discount_source === "ai" && "🤖 letto dall'AI"}
              {row.__discount_source === "calculated" && "🧮 calcolato dal totale netto"}
              {row.__discount_source === "supplier_memory" && "🧠 memoria fornitore (DDT precedenti)"}
              {row.__discount_source === "global" && "📋 sconto generale documento"}
              {row.__discount_source === "manual" && "✋ inserito a mano"}
            </div>
          )}
        </div>
      </FieldRow>

      {/* Live preview gross / discount / net */}
      {totals && (
        <div className="rounded-md bg-[#F4F1ED] border border-[#EAE7E1] p-2.5 text-xs space-y-1 font-mono">
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 text-[#5C5F5A]">
            <span className="min-w-0 break-words">Lordo</span>
            <span className="min-w-0 break-words text-right">€ {totals.gross}</span>
          </div>
          {discountPct > 0 && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 text-[#8B6518]">
              <span className="min-w-0 break-words">Sconto −{discountPct}%</span>
              <span className="min-w-0 break-words text-right">− € {totals.discount}</span>
            </div>
          )}
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 font-semibold text-[#2B5A3F] pt-1 border-t border-[#EAE7E1]">
            <span className="min-w-0 break-words">Totale netto</span>
            <span data-testid={`product-net-total-${row.__id}`} className="min-w-0 break-words text-right">€ {totals.net}</span>
          </div>
          {isWeight && netPricePerKg > 0 && (
            <div className="text-[10px] text-[#8E918C] pt-1 break-words">
              Costo netto: € {round2(netPricePerKg).toFixed(2)}/kg → salvato in magazzino
            </div>
          )}
          {!isWeight && netUnitPrice > 0 && (
            <div className="text-[10px] text-[#8E918C] pt-1 break-words">
              Costo netto: € {round2(netUnitPrice).toFixed(2)}/pz → salvato in magazzino
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-[#8E918C] px-1 leading-relaxed">
        💡 Il <strong>prezzo di acquisto</strong> dal fornitore (al netto dello sconto) viene salvato nel magazzino. Imposterai il prezzo di vendita in seguito — l&apos;app calcolerà il margine automaticamente.
      </div>

      <div className="flex items-center justify-between p-2.5 rounded-md bg-[#F4F1ED] border border-[#EAE7E1]">
        <span className="text-xs font-medium">Prodotto fresco</span>
        <Switch checked={!!row.is_fresh} onCheckedChange={(v) => onChange({ is_fresh: v })} />
      </div>
    </div>
  );
}
